import {includes, map} from 'lodash';
import {useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Checkbox, CopyText, Icon, Table, TableBody, TableCell, TableIdCell, TableRow, Toggle, Tooltip} from 'components';
import {CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE} from 'constants/client-connect';
import {UserContext} from 'context';
import {ClientConnectAutoRemove} from 'enums';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getAssignmentName} from 'services';
import {ClientConnect, UserSummary} from 'types';
import {findUserName, getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type Props = {
    loading?: boolean;
    connects: ClientConnect[];
    startIndex: number;
    groups: Group[];
    selectedIds: string[];
    isAllSelected: boolean;
    toggleDisabledInProgress: string[];
    users: UserSummary[];
    onUpdate: (connect: ClientConnect) => void;
    onDelete: (connect: ClientConnect) => void;
    onSendMessage: (connect: ClientConnect) => void;
    onToggleDisabled: (connect: ClientConnect) => void;
    onToggleSelection: (id: string) => void;
    onToggleSelectionAll: () => void;
    isSendMessageAvailable: (connect: ClientConnect) => boolean;
    onOpenScheduledMessageDetailsDialog: (connect: ClientConnect) => void;
};

export const ClientConnectTable = ({
    loading, connects, startIndex, groups, selectedIds, isAllSelected, toggleDisabledInProgress, users,
    onOpenScheduledMessageDetailsDialog, onToggleDisabled, onToggleSelection, onToggleSelectionAll, onUpdate, onDelete, onSendMessage,
    isSendMessageAvailable
}: Props) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);

    const headers = useMemo(() => [
        {
            children: <Checkbox
                size='small'
                name='selectAll'
                checked={isAllSelected}
                onChange={onToggleSelectionAll}
            />, className: 'tw-w-7'
        },
        {children: '#', className: 'tw-w-8'},
        {children: t(T_KEYS.FIELDS.ID), className: 'tw-w-32'},
        {children: t(T_KEYS.FIELDS.CLIENT_INFO), className: 'tw-w-36'},
        {children: `${t(T_KEYS.FIELDS.ASSIGNMENT)} / ${t(COMMON_T_KEYS.SESSION.LABELS.AGENT)}`, className: 'tw-w-36'},
        {children: t(T_KEYS.FIELDS.CONTACTS), className: 'tw-w-36'},
        {children: t(T_KEYS.FIELDS.COMMENTS), className: 'tw-w-36'},
        {children: t(T_KEYS.FIELDS.LINK), className: 'tw-w-16'},
        {children: t(T_KEYS.FIELDS.ENABLED), className: 'tw-w-20'},
        {children: t(T_KEYS.FIELDS.AUTO_REMOVE), className: 'tw-w-28'},
        {children: t(T_KEYS.FIELDS.SCHEDULED_MESSAGES), className: 'tw-w-24'},
        {children: t(T_KEYS.FIELDS.ACTIONS), className: 'tw-w-24 tw-text-right'},
    ], [isAllSelected, onToggleSelectionAll, t]);

    return (
        <Table
            headers={headers}
            className='-lg:tw-hidden'
            noData={!connects.length}
            loading={loading}
        >
            <TableBody>
                {map(connects, (connect: ClientConnect, index) => {
                    const assignmentName = getAssignmentName(
                        groups,
                        connect.assignmentInfo.groupId,
                        connect.assignmentInfo.subgroupId
                    );
                    const agentName = findUserName(t, users, {email: connect.agent?.email}) || connect.agent?.email;
                    const selected = selectedIds.includes(connect.id);
                    return (
                        <TableRow key={connect.id} selected={selected}>
                            <TableCell>
                                <Checkbox
                                    size='small'
                                    name={`checkbox-${connect.id}`}
                                    checked={selected}
                                    onChange={() => onToggleSelection(connect.id)}
                                />
                            </TableCell>
                            <TableCell className='tw-text-default-dimmed tw-text-nowrap'>
                                <div>{startIndex + index + 1}</div>
                            </TableCell>
                            <TableIdCell
                                item={connect}
                                timeZone={timeZone}
                                dateFormat={dateFormat}
                                timeFormat={timeFormat}
                                className='tw-text-default-dimmed tw-text-nowrap'
                            />
                            <TableCell>
                                <div className='tw-ts-base-bold'>{connect.userInfo.id}</div>
                                <div className='tw-text-sm tw-text-default-dimmed'>{getParticipantName(connect.userInfo)}</div>
                            </TableCell>
                            <TableCell>
                                {assignmentName &&
                                    <div className='tw-flex'>
                                        <Icon name='paper-clip' size='sm' className='tw-mr-1' />
                                        {assignmentName}
                                    </div>}
                                {agentName &&
                                    <div className='tw-flex'>
                                        <Icon name='agent-participant' size='sm' className='tw-mr-1' />
                                        {agentName}
                                    </div>}
                            </TableCell>
                            <TableCell>
                                {connect.contacts.phone &&
                                    <div className='tw-flex tw-items-center tw-mb-1 tw-break-all'>
                                        <Icon className='tw-mr-2' size='sm' name='phone' color='default-dimmed' />
                                        <div className='tw-text-default-dimmed'>{connect.contacts.phone}</div>
                                    </div>}
                                {connect.contacts.email &&
                                    <div className='tw-flex tw-items-center tw-break-all'>
                                        <Icon className='tw-mr-2' size='sm' name='mail' color='default-dimmed' />
                                        <div className='tw-text-default-dimmed'>{connect.contacts.email}</div>
                                    </div>}
                            </TableCell>
                            <TableCell>{connect.comments}</TableCell>
                            <TableCell>
                                <CopyText text={connect.link} iconOnly />
                            </TableCell>
                            <TableCell>
                                <Toggle
                                    size='small'
                                    className='tw-mt-1.5'
                                    name={`toggle-${connect.id}`}
                                    checked={!connect.disabled}
                                    disabled={includes(toggleDisabledInProgress, connect.id)}
                                    onChange={() => onToggleDisabled(connect)}
                                />
                            </TableCell>
                            <TableCell className='tw-text-default-dimmed'>
                                {connect.autoRemove !== ClientConnectAutoRemove.Never &&
                                    t(CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE[connect.autoRemove])}
                            </TableCell>
                            <TableCell>
                                {connect.scheduledMessages.length > 0 &&
                                    <div className='tw-flex'>
                                        <div className='tw-text-default-dimmed'>{connect.scheduledMessages.length}</div>
                                        <Icon
                                            button
                                            group
                                            name='info'
                                            color='default-dimmed'
                                            className='tw-ml-0.5'
                                            onClick={() => onOpenScheduledMessageDetailsDialog(connect)}
                                        />
                                    </div>}
                            </TableCell>
                            <TableCell>
                                <div className='tw-flex tw-justify-end'>
                                    {isSendMessageAvailable(connect) &&
                                        <Tooltip message={t(T_KEYS.TOOLTIPS.SEND_LINK)}>
                                            <Icon
                                                button
                                                className='tw-mr-2'
                                                name='reply'
                                                color='primary'
                                                onClick={() => onSendMessage(connect)}
                                            />
                                        </Tooltip>}
                                    <Icon
                                        button
                                        group
                                        name='edit'
                                        color='primary'
                                        onClick={() => onUpdate(connect)}
                                    />
                                    <Icon
                                        button
                                        group
                                        name='delete'
                                        color='alert'
                                        onClick={() => onDelete(connect)}
                                    />
                                </div>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};
